/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { hasClosest } from '@gh/core-util';
import { merge$ } from '@gh/rx';
import { map$ } from '@gh/rx/operators';
import { clone, uniqueId } from 'lodash';
import { DragulaService } from 'ng2-dragula';
import { Subscription } from 'rxjs';

export type DragulaOptions = any;

export const DELEGATED_DRAGULA_EVENTS = ['dropModel', 'removeModel', 'drag'];

/**
 * Creates default Dragula options where DnD handle should have provided selector.
 * Typically developer can use default selector 'dnd-pointer',
 * But when page has several dragula bags (especially organized hierarchically)
 * it is better to use different DnD selectors to overcome some internal dragula problems.
 */
export const createDragulaDndOptions = (handleSelector: string = 'dnd-pointer') => ({
  moves: (_: HTMLElement, __: HTMLElement, handle: HTMLElement): boolean =>
    hasClosest(handle, handleSelector),
});

/**
 * Lightweight wrapper around dragula. It simplifies usage of dragula library.
 */
@Directive({
  standalone: false,
  selector: '[ghDragulaContext]',
  exportAs: 'ghDragulaContext',
})
export class DragulaContextDirective implements OnInit, OnDestroy {
  @Input('ghDragulaContext') options: any;

  @Output() dropModel = new EventEmitter<any>();
  @Output() removeModel = new EventEmitter<any>();
  @Output() drag = new EventEmitter<any>();

  private initialized = false;
  private _name: string;
  private eventSubscription: Subscription;

  @Input('ghDragulaContextName')
  set name(name: string) {
    if (this.initialized) {
      throw new Error('Name of drake cannot be changed after it was initialized');
    }
    this._name = name;
  }

  get name(): string {
    return this._name;
  }

  constructor(private dragula: DragulaService) {
  }

  ngOnInit(): void {
    // We generate unique drake name if it was not provided by developer
    this._name = this._name || uniqueId('dragulaContainer:');
    // We clone drake options, because dragula mutates given object: it is better to avoid side effects
    this.dragula.createGroup(this._name, clone(this.options));
    this.initialized = true;

    this.eventSubscription = merge$(...DELEGATED_DRAGULA_EVENTS.map((eventName) =>
      (this.dragula as any)[eventName](this._name).pipe(
        map$((args: any) => [eventName, args.name, args.el, args.target, args.source, args.item, args.sourceModel, args.targetModel])))
    ).subscribe(([eventName, ...args]) => (this as any)[eventName].emit(args));
  }

  ngOnDestroy(): void {
    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
    }
    this.dragula.destroy(this._name);
  }
}
